/**
 * Demo Mode — Investor-Pitch Visibility Layer (UI-only)
 * =====================================================
 *
 * Estratégia: NÃO altera dados persistidos, governança, RLS, ledger,
 * auditoria nem integridade referencial. Apenas oculta, na camada de
 * aplicação, contratos não-demo nas LISTAGENS e AGREGADOS visualmente
 * expostos durante o pitch.
 *
 * Reversibilidade:
 *   • Default: ATIVO (ambiente de demo).
 *   • Override via env: VITE_DEMO_MODE = "false" desliga completamente.
 *   • Override em runtime: localStorage['netuno.demoMode'] = "false".
 *
 * Quando desativado, todos os helpers viram no-ops (passthrough total),
 * sem qualquer mudança de comportamento histórico.
 */

/**
 * IDs canônicos dos 3 contratos aprovados para o pitch a investidores.
 *   • Porto Açu — EPC avançado    (cluster c7c070ce…)
 *   • BR-040    — PPU intermediário (cluster 35e6bfb4…)
 *   • Maré      — PPU inicial       (cluster 35e6bfb4…)
 */
export const DEMO_CONTRACT_IDS = Object.freeze([
  'aa000001-0000-0000-0000-000000000001', // Porto Açu (EPC)
  'aa000001-0000-0000-0000-000000000003', // BR-040 (PPU)
  'aa000001-0000-0000-0000-000000000004', // Maré (PPU)
]) as readonly string[];

/**
 * Mapa estático contrato→cluster do conjunto demo. Usado APENAS em runtime
 * para alinhar a visibilidade ao cluster do usuário demo logado, evitando
 * bloqueios silenciosos de RLS no fluxo ponta-a-ponta. Nenhum dado persistido
 * é alterado — é um filtro de leitura na camada de aplicação.
 */
export const DEMO_CONTRACT_CLUSTER_MAP: Readonly<Record<string, string>> = Object.freeze({
  'aa000001-0000-0000-0000-000000000001': 'c7c070ce-70b2-43be-b427-6bae55fc41a3',
  'aa000001-0000-0000-0000-000000000003': '35e6bfb4-7afb-4c2b-97ab-f29a8bd9312d',
  'aa000001-0000-0000-0000-000000000004': '35e6bfb4-7afb-4c2b-97ab-f29a8bd9312d',
});

const DEMO_SET = new Set<string>(DEMO_CONTRACT_IDS);

/* ──────────────────────────────────────────────────────────────────────────
 * Demo Cluster Context (runtime-only, in-memory)
 * --------------------------------------------------------------------------
 * Setado pelo AuthContext quando um usuário `is_demo = true` autentica. Não
 * persiste, não vai ao backend, não altera RLS. Quando definido, restringe a
 * lista visível de contratos demo àqueles cujo cluster bate com o cluster_id
 * do usuário demo — garantindo coerência de fluxo (execução → qualidade →
 * planejamento → medição) sem mismatch de RLS. Reversível removendo o setter.
 * ────────────────────────────────────────────────────────────────────────── */
let demoClusterContext: string | null = null;
const demoClusterListeners = new Set<() => void>();

export function setDemoClusterContext(clusterId: string | null | undefined): void {
  const next = clusterId ?? null;
  if (next === demoClusterContext) return;
  demoClusterContext = next;
  demoClusterListeners.forEach(fn => { try { fn(); } catch { /* noop */ } });
}

export function getDemoClusterContext(): string | null {
  return demoClusterContext;
}

export function subscribeDemoClusterContext(fn: () => void): () => void {
  demoClusterListeners.add(fn);
  return () => { demoClusterListeners.delete(fn); };
}

/**
 * Lista efetiva de contratos demo visíveis. Se houver `demoClusterContext`
 * definido, restringe ao subconjunto coerente com aquele cluster. Caso
 * contrário, devolve o conjunto completo (compatível com comportamento anterior).
 */
export function getVisibleDemoContractIds(): readonly string[] {
  if (!demoClusterContext) return DEMO_CONTRACT_IDS;
  const filtered = DEMO_CONTRACT_IDS.filter(
    id => DEMO_CONTRACT_CLUSTER_MAP[id] === demoClusterContext,
  );
  // Fallback de segurança: se nenhum contrato bater, devolve o conjunto pleno
  // para evitar esconder 100% da UI por configuração inesperada.
  return filtered.length > 0 ? filtered : DEMO_CONTRACT_IDS;
}

function readEnvFlag(): boolean | null {
  try {
    const raw = (import.meta as any)?.env?.VITE_DEMO_MODE;
    if (raw === undefined || raw === null || raw === '') return null;
    const v = String(raw).toLowerCase();
    if (['false', '0', 'off', 'no'].includes(v)) return false;
    if (['true', '1', 'on', 'yes'].includes(v)) return true;
  } catch { /* noop */ }
  return null;
}

function readRuntimeFlag(): boolean | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage?.getItem('netuno.demoMode');
    if (raw === null || raw === undefined) return null;
    const v = raw.toLowerCase();
    if (['false', '0', 'off', 'no'].includes(v)) return false;
    if (['true', '1', 'on', 'yes'].includes(v)) return true;
  } catch { /* noop */ }
  return null;
}

/** Default-on. Runtime override > env override > default(true). */
export function isDemoMode(): boolean {
  const rt = readRuntimeFlag();
  if (rt !== null) return rt;
  const env = readEnvFlag();
  if (env !== null) return env;
  return true;
}

export function getDemoContractIds(): readonly string[] {
  return getVisibleDemoContractIds();
}

/** Aplica `.in(column, VISIBLE_DEMO_IDS)` quando demo mode ativo. */
export function applyDemoContractFilter<T extends { in: (col: string, vals: unknown[]) => T }>(
  query: T,
  column: string = 'id',
): T {
  if (!isDemoMode()) return query;
  return query.in(column, getVisibleDemoContractIds() as unknown as string[]);
}

/** Filtra um array em memória por chave de contrato_id. */
export function filterDemoArray<T extends Record<string, any>>(
  rows: T[] | null | undefined,
  key: string = 'contrato_id',
): T[] {
  if (!rows) return [];
  if (!isDemoMode()) return rows;
  const visible = new Set(getVisibleDemoContractIds());
  return rows.filter(r => visible.has(r?.[key]));
}

export function isDemoContractId(id: string | null | undefined): boolean {
  if (!isDemoMode()) return true;
  if (!id) return false;
  if (!DEMO_SET.has(id)) return false;
  const ctx = getDemoClusterContext();
  if (!ctx) return true;
  return DEMO_CONTRACT_CLUSTER_MAP[id] === ctx;
}
